import { PureComponent, ReactNode } from 'react';
import getAllOrders, { Order } from '../api/getAllOrders';
import { captureError } from './captureError';
import { track } from '../tracking';
import moment from 'moment';
import OrderMedicine from '../components/prescriptionPad/rightPanel/finishScreen/OrderMedicine';
import BookLabTests from '../components/prescriptionPad/rightPanel/finishScreen/BookLabTests';
import { getDocIds, getPatientByLocalId } from '../store/idbHandler';
import { Patient } from '../store/types/patient';
import { AgeInYears, getAgeInYears } from '../store/utils';
import {
    GetMedicineOrderStatus,
    getPincode,
} from '../components/prescriptionPad/rightPanel/finishScreen/OrderMedicineUtils';
import { GetLabOrderStatus } from '../components/prescriptionPad/rightPanel/finishScreen/BookLabTestsUtils';
import { Data as MedicineData } from '../api/getMedicinesOrder';
import { Data as LabData } from '../api/getLabTestsOrder';
import { ACTIVE_TAB, ActiveTab } from './HEDashboard';

interface Props {
    plId: string;
}

interface State {
    pharmaOrders?: Order[];
    labOrders?: Order[];
    activeTab: ActiveTab;
    currentOrder?: Order;
    patient?: Patient;
    isFetchingOrders: boolean;
}

class OrdersRightPanel extends PureComponent<Props> {
    state: State = {
        activeTab: ACTIVE_TAB.LABS,
        isFetchingOrders: true,
    };

    componentDidMount = (): void => {
        const { fetchAllOrders, fetchPatient } = this;
        fetchPatient();
        fetchAllOrders();
    };

    fetchPatient = async (): Promise<void> => {
        const { plId } = this.props;

        try {
            const patient = await getPatientByLocalId(plId);
            this.setState({
                patient,
            });
        } catch (error) {
            captureError(error as Error);
            track('ERROR', {
                on: 'getPatientByLocalId',
                error: (error as Error).message,
            });
        }
    };

    fetchAllOrders = async (): Promise<void> => {
        const { plId } = this.props;
        const docIds = getDocIds().join(',');

        try {
            const { success, pharma_orders = [], lab_orders = [] } = await getAllOrders(
                docIds,
                plId,
            );
            if (success) {
                if (pharma_orders.length > 0 && lab_orders.length === 0) {
                    this.setState({
                        pharmaOrders: pharma_orders,
                        labOrders: lab_orders,
                        activeTab: ACTIVE_TAB.MEDS,
                        currentOrder: pharma_orders[0],
                    });
                } else {
                    this.setState({
                        pharmaOrders: pharma_orders,
                        labOrders: lab_orders,
                        currentOrder: lab_orders[0],
                    });
                }
            }
        } catch (error) {
            captureError(error as Error);
            track('ERROR', {
                on: 'getAllOrders',
                error: (error as Error).message,
            });
        } finally {
            this.setState({
                isFetchingOrders: false,
            });
        }
    };

    tabChangeHandler = (newTab: ActiveTab): void => {
        const { activeTab, pharmaOrders, labOrders } = this.state;
        if (activeTab === newTab) return;
        const order = newTab === ACTIVE_TAB.MEDS ? pharmaOrders?.[0] : labOrders?.[0];
        this.setState({
            activeTab: newTab,
            currentOrder: order,
        });
        track('TAB_CHANGE_ALL_ORDERS_PATIENT_DETAILS', { activeTab: newTab });
    };

    viewOrder = (order: Order): void => {
        const { activeTab } = this.state;
        this.setState({
            currentOrder: order,
        });
        track('VIEW_ORDER_PATIENT_DETAILS', { activeTab, id: order.order_id });
    };

    closeMe = (): void => console.log('closeMe');

    render(): ReactNode {
        const {
            activeTab,
            pharmaOrders,
            labOrders,
            currentOrder,
            patient,
            isFetchingOrders,
        } = this.state;
        const { tabChangeHandler, viewOrder, closeMe } = this;
        const orders = activeTab === ACTIVE_TAB.MEDS ? pharmaOrders : labOrders;
        const allOrders = Array.isArray(orders) ? orders : [];
        const sortedOrders = allOrders.sort((a, b) => {
            return moment(a.created_at).isAfter(moment(b.created_at)) ? -1 : 1;
        });
        const age = getAgeInYears((patient?.personal.age || {}) as AgeInYears) || 18;
        const formData = patient?.formData || [];
        const pxId = currentOrder?.pxid || '';
        const patientId = patient?.id || patient?.localId || '';
        const patientName = [patient?.personal?.name?.f, patient?.personal?.name?.l]
            .filter(Boolean)
            .join(' ');
        const phoneNumber = patient?.personal?.phone?.n || '';
        const gender = patient?.personal?.gender?.[0]?.toUpperCase() || '';
        const orderId = currentOrder?.order_id || '';
        const clinicPin = getPincode(patient);

        return (
            <div className="flex col-span-10 overflow-hidden bg-white">
                <div className="w-30p border-l border-r border-darwin-neutral-100">
                    <div className="flex flex-col h-full w-full bg-neutral-9 relative">
                        <div className="flex flex-col absolute inset-0 overflow-y-auto">
                            <div className="flex flex-col space-y-18 w-full">
                                <div className="flex items-center justify-center space-x-16 font-400 text-15 bg-white sticky top-0 z-1 pad-shadow-v2">
                                    <button
                                        className={`border-b-2 ${
                                            activeTab === ACTIVE_TAB.LABS
                                                ? 'font-700 text-darwin-primary-500 border-darwin-primary-500'
                                                : 'border-white'
                                        } flex items-center space-x-8 px-22 py-13 disabled:cursor-not-allowed`}
                                        onClick={() => tabChangeHandler(ACTIVE_TAB.LABS)}
                                        disabled={isFetchingOrders}
                                    >
                                        <span>Labs</span>
                                    </button>
                                    <button
                                        className={`border-b-2 ${
                                            activeTab === ACTIVE_TAB.MEDS
                                                ? 'font-700 text-darwin-primary-500 border-darwin-primary-500'
                                                : 'border-white'
                                        } flex items-center space-x-8 px-22 py-13 disabled:cursor-not-allowed`}
                                        onClick={() => tabChangeHandler(ACTIVE_TAB.MEDS)}
                                        disabled={isFetchingOrders}
                                    >
                                        <span>Pharmacy</span>
                                    </button>
                                </div>
                                {isFetchingOrders ? (
                                    <div className="absolute inset-0 pt-14 flex flex-col items-center justify-center space-y-12">
                                        <svg
                                            className="w-24 h-24 fill-darwin-primary-500 animate-spin"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 512 512"
                                        >
                                            <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                        </svg>
                                    </div>
                                ) : sortedOrders.length === 0 ? (
                                    <div className="space-y-16 px-16">
                                        <p className="leading-5 text-12 text-neutral-5 font-700">
                                            {moment().format('MMMM YYYY')}
                                        </p>
                                        <div className="flex flex-col space-y-18">
                                            {Array.from(Array(2)).map((i) => (
                                                <div
                                                    key={i}
                                                    className="flex items-center w-full space-x-16"
                                                >
                                                    <div className="w-22 h-46 text-center font-700 bg-neutral-7"></div>
                                                    <div className="flex items-center justify-between h-46 p-8 leading-6 bg-neutral-7 w-90p rounded-32"></div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : null}
                                {sortedOrders.map((order, i) => (
                                    <div
                                        className={`space-y-16 w-full px-16 ${
                                            i === sortedOrders.length - 1 ? 'pb-16' : ''
                                        }`}
                                    >
                                        {(i === 0 ||
                                            moment(order.created_at).format('MMMM YYYY') !==
                                                moment(sortedOrders[i - 1].created_at).format(
                                                    'MMMM YYYY',
                                                )) && (
                                            <p className="leading-5 text-12 text-neutral-5 font-700">
                                                {moment(order.created_at).format('MMMM YYYY')}
                                            </p>
                                        )}
                                        <div
                                            onClick={() => viewOrder(order)}
                                            className="flex items-center space-x-16 cursor-pointer w-full"
                                        >
                                            <div className="text-center font-700">
                                                <p
                                                    className={
                                                        currentOrder?.order_id !== order.order_id
                                                            ? 'text-18 text-neutral-5'
                                                            : 'text-18 text-neutral-1'
                                                    }
                                                >
                                                    {new Date(order.created_at).getDate()}
                                                </p>
                                                <p className="text-10 text-neutral-5">
                                                    {moment(order.created_at).format('ddd')}
                                                </p>
                                            </div>
                                            <div
                                                className={`flex-1 flex items-center p-8 leading-6 bg-white rounded-32 ${
                                                    currentOrder?.order_id === order.order_id
                                                        ? 'shadow-tight-3 transform-gpu scale-103'
                                                        : 'patient-details-visit-shadow hover:shadow-tight-3 hover:scale-103 transition-all transform-gpu duration-300 ease-in-out'
                                                }`}
                                            >
                                                <div
                                                    className={'flex items-center flex-1 space-x-8'}
                                                >
                                                    <div className="flex items-center justify-center rounded-full w-28 h-28 bg-docM-tab-bg text-docM-rad-main text-11">
                                                        {activeTab === ACTIVE_TAB.MEDS ? (
                                                            <svg
                                                                className="w-14 h-14"
                                                                viewBox="0 0 19 15"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                fill="#bc5c7c"
                                                            >
                                                                <path d="M16.5312 5.46875C14.5625 3.53125 11.4062 3.53125 9.4375 5.46875C7.5 7.4375 7.5 10.5938 9.4375 12.5625C11.4062 14.5 14.5625 14.5 16.5312 12.5625C18.4688 10.5938 18.4688 7.4375 16.5312 5.46875ZM10.0312 7.125L14.875 11.9688C11.7188 13.9688 8.03125 10.2812 10.0312 7.125ZM15.9375 10.9062L11.0938 6.0625C14.25 4.0625 17.9688 7.75 15.9375 10.9062ZM3.5 0C1.5625 0 0 1.59375 0 3.5V10.4688C0 12.4062 1.5625 13.9375 3.5 13.9375C5.40625 13.9375 7 12.375 7 10.4688V3.5C7 1.59375 5.40625 0 3.5 0ZM5.5 7H1.5V3.5C1.5 0.84375 5.5 0.84375 5.5 3.5V7Z" />
                                                            </svg>
                                                        ) : (
                                                            <svg
                                                                className="w-14 h-14"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                viewBox="0 0 512 512"
                                                                fill="#bc5c7c"
                                                            >
                                                                <path d="M504.1 183l-176-176C324.3 2.344 318.1 0 312 0s-12.28 2.344-16.97 7.031c-9.375 9.375-9.375 24.56 0 33.94l15.16 15.16l-277.3 276.5c-38.75 38.75-45.13 102-9.375 143.5C44.08 500 72.76 512 101.5 512h.4473c26.38 0 52.75-9.1 72.88-30.12l281-280.1l15.19 15.19C475.7 221.7 481.8 224 488 224s12.28-2.344 16.97-7.031C514.3 207.6 514.3 192.4 504.1 183zM333.4 256H177.7l166.3-165.9l77.82 77.73L333.4 256z"></path>
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <div
                                                        className={`flex-1 flex items-center justify-between space-x-2 text-15 w-full font-700
                                                            ${
                                                                currentOrder?.order_id ===
                                                                order.order_id
                                                                    ? ' text-neutral-1'
                                                                    : ' text-neutral-5'
                                                            }
                                                        `}
                                                    >
                                                        <p className="text-12 truncate-overflow-1">
                                                            {order.orderName ||
                                                                `₹ ${order.paidAmount}`}
                                                        </p>
                                                        <div
                                                            className="whitespace-nowrap"
                                                            style={{ transform: 'scale(0.8)' }}
                                                        >
                                                            {activeTab === ACTIVE_TAB.MEDS ? (
                                                                <GetMedicineOrderStatus
                                                                    order_details={
                                                                        order as MedicineData
                                                                    }
                                                                    removeBorder
                                                                />
                                                            ) : (
                                                                <GetLabOrderStatus
                                                                    order_details={order as LabData}
                                                                    removeBorder
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex justify-center items-center w-16 h-16">
                                                    <svg
                                                        width="14"
                                                        height="14"
                                                        className={
                                                            currentOrder?.order_id !==
                                                            order.order_id
                                                                ? 'fill-neutral-1 cursor-pointer fill-neutral-1'
                                                                : 'fill-neutral-5 cursor-pointer fill-neutral-5'
                                                        }
                                                        role="img"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 320 512"
                                                    >
                                                        <path d="M113.3 47.41l183.1 191.1c4.469 4.625 6.688 10.62 6.688 16.59s-2.219 11.97-6.688 16.59l-183.1 191.1c-9.152 9.594-24.34 9.906-33.9 .7187c-9.625-9.125-9.938-24.38-.7187-33.91l168-175.4L78.71 80.6c-9.219-9.5-8.906-24.78 .7187-33.91C88.99 37.5 104.2 37.82 113.3 47.41z"></path>
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="w-70p">
                    {patient && currentOrder && activeTab ? (
                        activeTab === ACTIVE_TAB.LABS ? (
                            <BookLabTests
                                closeMe={closeMe}
                                formData={formData}
                                pxid={pxId}
                                patientid={patientId}
                                name={patientName}
                                phone={phoneNumber}
                                gender={gender}
                                age={age}
                                orderId={orderId}
                                key={orderId}
                                hideBackButton
                            />
                        ) : (
                            <OrderMedicine
                                closeMe={closeMe}
                                formData={formData}
                                pxid={pxId}
                                patientId={patientId}
                                name={patientName}
                                phone={phoneNumber}
                                gender={gender}
                                age={age}
                                orderId={orderId}
                                key={orderId}
                                clinicPin={clinicPin}
                                hideBackButton
                            />
                        )
                    ) : (
                        <div className="flex flex-1 flex-col items-center justify-center h-full space-y-12 select-none">
                            <div>
                                <svg
                                    className="w-42 h-48 fill-neutral-3"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 640 512"
                                >
                                    <path d="M24 0C10.7 0 0 10.7 0 24S10.7 48 24 48H76.1l60.3 316.5c2.2 11.3 12.1 19.5 23.6 19.5H320.7c-.5-5.3-.7-10.6-.7-16c0-10.9 1-21.6 2.9-32h-143l-9.1-48H339.2c29.1-57 88.4-96 156.8-96c14.3 0 28.3 1.7 41.6 4.9l35-124.3C578.3 52.3 563 32 541.8 32H122l-2.4-12.5C117.4 8.2 107.5 0 96 0H24zM176 512a48 48 0 1 0 0-96 48 48 0 1 0 0 96zm320 0a144 144 0 1 0 0-288 144 144 0 1 0 0 288zm59.3-180.7L518.6 368l36.7 36.7c6.2 6.2 6.2 16.4 0 22.6s-16.4 6.2-22.6 0L496 390.6l-36.7 36.7c-6.2 6.2-16.4 6.2-22.6 0s-6.2-16.4 0-22.6L473.4 368l-36.7-36.7c-6.2-6.2-6.2-16.4 0-22.6s16.4-6.2 22.6 0L496 345.4l36.7-36.7c6.2-6.2 16.4-6.2 22.6 0s6.2 16.4 0 22.6z" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-neutral-3">No orders for this patient yet</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default OrdersRightPanel;
